import React from "react";
import { loadMunicipiosGeojson } from "./geojson";
import type { Municipality, Region } from "./types";

// O Google Maps e carregado dinamicamente (so quando a aba Mapa abre) e o SDK
// e injetado uma unica vez. A geometria dos municipios vem do arquivo estatico
// /municipios.geojson (malha IBGE) e a cor de cada cidade e aplicada em tempo
// de execucao, casando o codigo IBGE com a regiao vinda do banco.

declare const google: any;

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
const DEFAULT_COLOR = "#64748b";

type Status = "no-key" | "loading" | "ready" | "error";

type HoverInfo = {
  code: string;
  clientX: number;
  clientY: number;
};

type CustomerCity = {
  cidade: string;
  estado: string;
  count: number;
};

// O destaque sempre esmaece as cidades que NAO sao alvo. O alvo pode ser as
// cidades com cliente ("customers") ou as sem cliente ("non-customers").
type HighlightTarget = "customers" | "non-customers";

const HIGHLIGHT_TARGETS: Array<{ id: HighlightTarget; label: string }> = [
  { id: "customers", label: "clientes" },
  { id: "non-customers", label: "não clientes" },
];

let mapsPromise: Promise<void> | null = null;

function loadGoogleMaps(key: string): Promise<void> {
  if (mapsPromise) return mapsPromise;
  mapsPromise = new Promise<void>((resolve, reject) => {
    const callbackName = "__srInitGoogleMaps";
    (window as unknown as Record<string, unknown>)[callbackName] = () => resolve();
    const script = document.createElement("script");
    script.id = "google-maps-sdk";
    script.async = true;
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}` +
      `&callback=${callbackName}&language=pt-BR&region=BR`;
    script.onerror = () => reject(new Error("Falha ao carregar o Google Maps"));
    document.head.appendChild(script);
  });
  return mapsPromise;
}

// Chave normalizada (sem acento / apostrofo / pontuacao) para casar cidade do
// cadastro ("Santana do Livramento") com o municipio IBGE ("Sant'Ana do Livramento").
function cityKey(name: string, uf: string): string {
  const normalized = name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/['’`.]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return `${normalized}|${uf.trim().toUpperCase()}`;
}

type StyleSettings = {
  highlightOn: boolean;
  target: HighlightTarget;
  customerCodes: Set<string>;
};

function styleFor(code: string, color: string, settings: StyleSettings) {
  const base = {
    fillColor: color,
    fillOpacity: 0.4,
    strokeColor: color,
    strokeOpacity: 0.9,
    strokeWeight: 0.6,
    zIndex: 1,
  };
  if (!settings.highlightOn) return base;

  const isCustomer = settings.customerCodes.has(code);
  const isTarget = settings.target === "customers" ? isCustomer : !isCustomer;
  return isTarget
    ? { ...base, fillOpacity: 0.78, zIndex: 3 }
    : { ...base, fillOpacity: 0.08, strokeOpacity: 0.2 };
}

export function MapView({ regions, municipalities }: { regions: Region[]; municipalities: Municipality[] }) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<any>(null);
  const dataRef = React.useRef<any>(null);
  const [status, setStatus] = React.useState<Status>(GOOGLE_MAPS_API_KEY ? "loading" : "no-key");
  const [hovered, setHovered] = React.useState<HoverInfo | null>(null);
  const [customerCities, setCustomerCities] = React.useState<CustomerCity[]>([]);
  const [customerAvailable, setCustomerAvailable] = React.useState(true);
  const [highlightOn, setHighlightOn] = React.useState(false);
  const [target, setTarget] = React.useState<HighlightTarget>("customers");

  const regionById = React.useMemo(() => new Map(regions.map((item) => [item.id, item])), [regions]);
  const muniByCode = React.useMemo(
    () => new Map(municipalities.map((item) => [item.ibge_code, item])),
    [municipalities],
  );
  const colorByCode = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const city of municipalities) {
      map.set(city.ibge_code, regionById.get(city.region_id)?.color_hex || DEFAULT_COLOR);
    }
    return map;
  }, [municipalities, regionById]);

  // Casa as cidades-cliente (cadastros) com os municipios IBGE por nome + UF.
  const customerData = React.useMemo(() => {
    const byKey = new Map<string, string>();
    for (const muni of municipalities) byKey.set(cityKey(muni.name, muni.uf), muni.ibge_code);

    const codes = new Set<string>();
    const counts = new Map<string, number>();
    let matchedCustomers = 0;
    const unmatched: CustomerCity[] = [];
    for (const city of customerCities) {
      const code = byKey.get(cityKey(city.cidade, city.estado));
      if (code) {
        codes.add(code);
        counts.set(code, (counts.get(code) || 0) + city.count);
        matchedCustomers += city.count;
      } else {
        unmatched.push(city);
      }
    }
    return { codes, counts, matchedCustomers, unmatched };
  }, [customerCities, municipalities]);

  // Refs para o callback de estilo da camada sempre ler a versao mais recente.
  const colorRef = React.useRef(colorByCode);
  colorRef.current = colorByCode;
  const settingsRef = React.useRef<StyleSettings>({
    highlightOn,
    target,
    customerCodes: customerData.codes,
  });
  settingsRef.current = { highlightOn, target, customerCodes: customerData.codes };

  // Busca as cidades com cliente (so quando o mapa abre).
  React.useEffect(() => {
    let cancelled = false;
    fetch("/api/customer-cities", { credentials: "include" })
      .then((res) => (res.ok ? (res.json() as Promise<{ available?: boolean; cities?: CustomerCity[] }>) : null))
      .then((data) => {
        if (cancelled || !data) return;
        setCustomerCities(data.cities || []);
        setCustomerAvailable(data.available !== false);
      })
      .catch(() => {
        if (!cancelled) setCustomerAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) return;
    let cancelled = false;

    loadGoogleMaps(GOOGLE_MAPS_API_KEY)
      .then(async () => {
        if (cancelled || !containerRef.current) return;

        const map = new google.maps.Map(containerRef.current, {
          center: { lat: -28.5, lng: -51.5 },
          zoom: 6,
          minZoom: 4,
          streetViewControl: false,
          mapTypeControl: true,
          mapTypeControlOptions: { position: google.maps.ControlPosition.TOP_RIGHT },
          fullscreenControl: true,
          gestureHandling: "greedy",
        });
        mapRef.current = map;

        const geojson = await loadMunicipiosGeojson();
        if (cancelled) return;

        map.data.addGeoJson(geojson);
        dataRef.current = map.data;
        applyStyle(map.data);

        map.data.addListener("mouseover", (event: any) => {
          map.data.overrideStyle(event.feature, { strokeWeight: 2.6, zIndex: 6 });
          const domEvent = event.domEvent as MouseEvent | undefined;
          setHovered({
            code: String(event.feature.getProperty("c")),
            clientX: domEvent?.clientX ?? 0,
            clientY: domEvent?.clientY ?? 0,
          });
        });
        map.data.addListener("mousemove", (event: any) => {
          const domEvent = event.domEvent as MouseEvent | undefined;
          if (!domEvent) return;
          setHovered((prev) =>
            prev ? { ...prev, clientX: domEvent.clientX, clientY: domEvent.clientY } : prev,
          );
        });
        map.data.addListener("mouseout", () => {
          map.data.revertStyle();
          setHovered(null);
        });

        if (!cancelled) setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reaplica o estilo quando muda cor, toggle de clientes, estilo de destaque ou casamento.
  React.useEffect(() => {
    if (status === "ready" && dataRef.current) applyStyle(dataRef.current);
  }, [colorByCode, status, highlightOn, target, customerData]);

  function applyStyle(data: any) {
    data.setStyle((feature: any) => {
      const code = String(feature.getProperty("c"));
      const color = colorRef.current.get(code) || DEFAULT_COLOR;
      return styleFor(code, color, settingsRef.current);
    });
  }

  const hoveredCity = hovered ? muniByCode.get(hovered.code) : null;
  const hoveredRegion = hoveredCity ? regionById.get(hoveredCity.region_id) : null;
  const hoveredCustomers = hovered ? customerData.counts.get(hovered.code) || 0 : 0;

  return (
    <div className="map-view">
      <div ref={containerRef} className="map-canvas" />

      {status !== "ready" ? (
        <div className="map-overlay">
          {status === "loading" ? (
            <>
              <div className="spinner" />
              <p>Carregando mapa...</p>
            </>
          ) : null}
          {status === "no-key" ? (
            <div className="map-notice">
              <strong>Configuracao pendente</strong>
              <p>
                Defina a variavel <code>VITE_GOOGLE_MAPS_API_KEY</code> com a chave do Google Maps
                (no <code>.env</code> local e nas variaveis de build do Cloudflare Pages) para
                exibir o mapa.
              </p>
            </div>
          ) : null}
          {status === "error" ? (
            <div className="map-notice">
              <strong>Nao foi possivel carregar o mapa</strong>
              <p>Verifique a chave do Google Maps e a conexao, depois recarregue a pagina.</p>
            </div>
          ) : null}
        </div>
      ) : null}

      {status === "ready" ? (
        <div className="map-controls">
          <div className="map-toggle">
            <input
              id="map-highlight-toggle"
              type="checkbox"
              checked={highlightOn}
              onChange={(event) => setHighlightOn(event.target.checked)}
            />
            <label htmlFor="map-highlight-toggle">Destacar</label>
            <select
              className="map-target-select"
              value={target}
              onChange={(event) => setTarget(event.target.value as HighlightTarget)}
            >
              {HIGHLIGHT_TARGETS.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          {highlightOn ? (
            <div className="map-controls__meta">
              {customerAvailable ? (
                <>
                  <span>
                    <strong>
                      {(target === "customers"
                        ? customerData.codes.size
                        : municipalities.length - customerData.codes.size
                      ).toLocaleString("pt-BR")}
                    </strong>{" "}
                    {target === "customers" ? "cidades com cliente" : "cidades sem cliente"}
                  </span>
                  {customerData.unmatched.length > 0 ? (
                    <small>{customerData.unmatched.length} não localizadas no mapa</small>
                  ) : null}
                </>
              ) : (
                <small>Base de clientes indisponível.</small>
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      {status === "ready" ? (
        <div className="map-legend">
          {regions.map((region) => (
            <div className="map-legend__row" key={region.id}>
              <i style={{ background: region.color_hex }} />
              <span>{region.name}</span>
              <small>{region.current_responsible_name}</small>
            </div>
          ))}
        </div>
      ) : null}

      {hovered && hoveredCity ? (
        <div className="map-tooltip" style={{ left: hovered.clientX + 14, top: hovered.clientY + 14 }}>
          <strong>{hoveredCity.name}</strong>
          <span className="map-tooltip__uf">{hoveredCity.uf}</span>
          <div className="map-tooltip__region">
            <i style={{ background: hoveredRegion?.color_hex || DEFAULT_COLOR }} />
            <span>
              {hoveredRegion ? `${hoveredRegion.name} (${hoveredRegion.current_responsible_name})` : "Sem região"}
            </span>
          </div>
          {hoveredCustomers > 0 ? (
            <div className="map-tooltip__customers">
              {hoveredCustomers.toLocaleString("pt-BR")} cliente{hoveredCustomers > 1 ? "s" : ""}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
